import { useEffect, useRef, useState } from "react";

import { motion, useReducedMotion } from "motion/react";

import { ReactComponent as LoadingIcon } from "@/assets/svg/loading-spinner.svg";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { isAssemblyAIScribe } from "@/constants/live-transcription";
import { cn } from "@/utils/ui";

import { WaveSpinner } from "@components/WaveSpinner";

/** One timestamped turn in the live plain-transcript feed. */
export type LiveTranscriptionSegment = {
  text: string;
  /** Pre-formatted start timestamp, e.g. "00:12:48". */
  timestamp: string;
  /** Provisional live speaker label ("A", "B", …) — AssemblyAI only. */
  speaker?: string;
  /** Stable React key, so a speaker revision re-labels the row instead of remounting. */
  key?: string;
  /** "Reconnected" separator; speaker labels restart with each connection. */
  divider?: boolean;
};

type LiveTranscriptionFeedProps = {
  segments: LiveTranscriptionSegment[];
  /**
   * The in-progress (interim) hypothesis for the current turn — rendered greyed
   * beneath the finalised turns (compact layout: with a caret cursor + "…"). Omitted once the
   * turn finalises into `segments`.
   */
  interim?: LiveTranscriptionSegment | null;
  /**
   * Drives the loading/status footer under the feed: "connecting" (session
   * handshake), "restoring" (re-hydrating the feed after returning to the tab
   * mid-session), "paused" (transcription suspended), or "listening" (recording
   * but no interim yet — e.g. the gap right after resume, so it doesn't look
   * frozen). Omit/"idle" to show nothing.
   */
  state?: "idle" | "connecting" | "restoring" | "listening" | "paused";
  layout?: "compact" | "transcript";
  /** Render the provisional live speaker labels. */
  showSpeakers?: boolean;
};

/**
 * The plain live-transcription feed shown in the Scribe session panel
 * (MDRS-116). Finalised turns render as a timestamped line of body text; the
 * interim line is greyed and trailed by a blinking caret cursor and "…".
 * Auto-scrolls to the newest turn. Rows carry a provisional "Speaker A/B"
 * label only when the live engine supplies one (AssemblyAI); the persisted,
 * batch-diarized transcript (loaded once the session ends) remains the source
 * of record for speaker-labelled text.
 */
export const LiveTranscriptionFeed = ({
  segments,
  interim,
  state = "idle",
  layout = isAssemblyAIScribe() ? "transcript" : "compact",
  showSpeakers = true,
}: LiveTranscriptionFeedProps) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isTranscript = layout === "transcript";
  const following = useStickToBottom(viewportRef, isTranscript);

  useEffect(() => {
    if (isTranscript) return;
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [segments, interim, state, isTranscript]);

  useFollowContent(viewportRef, contentRef, isTranscript && following);

  const reduceMotion = useReducedMotion() ?? false;
  const rowKeys = isTranscript ? segments.map(rowKey) : [];
  const entranceSlot = useRowEntrances(rowKeys);

  // Stays mounted while listening so it doesn't pop in/out between turns.
  const showLivePanel = isTranscript && (!!interim || state === "listening");
  const liveSegment: LiveTranscriptionSegment = interim ?? {
    text: "",
    timestamp: "",
  };

  const isEmpty =
    segments.length === 0 && (isTranscript ? !showLivePanel : !interim);
  // Show the status footer only when there's no interim line already conveying
  // activity — and never while idle.
  const showStatus = state !== "idle" && (isTranscript ? !showLivePanel : !interim);
  const Row = isTranscript ? TranscriptRow : FeedRow;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Full-bleed subheader bar — matches the card header treatment. */}
      <div className="flex shrink-0 items-center justify-between border-y border-border bg-background-raised px-5 py-3">
        <h3 className="text-sm font-semibold text-background-dark">
          Live transcription
        </h3>
        {/* Auto-scrolling pill — primary-50 bg, primary text. In the
            transcript feed it reads "Paused" while the user has scrolled up. */}
        {following ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-2.5 py-1 text-[10px] font-medium text-primary">
            <span className="size-1.5 rounded-full bg-primary" aria-hidden="true" />
            Auto-scrolling
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-background-muted px-2.5 py-1 text-[10px] font-medium text-foreground-muted">
            <span
              className="size-1.5 rounded-full bg-foreground-muted"
              aria-hidden="true"
            />
            Paused
          </span>
        )}
      </div>

      <ScrollArea
        ref={viewportRef}
        className="min-h-0 flex-1 overflow-hidden bg-background-white"
      >
        <div
          ref={contentRef}
          className={cn(
            "flex min-h-full flex-col px-4 py-4",
            layout === "transcript" ? "gap-6" : "gap-3"
          )}
        >
          {isEmpty ? (
            <FeedEmptyState state={state} />
          ) : isTranscript ? (
            <>
              {/* Announces committed lines only; the in-progress line would be too noisy. */}
              <div
                role="log"
                aria-live="polite"
                aria-relevant="additions"
                aria-label="Live transcript"
                className="contents"
              >
                {segments.map((segment, index) => (
                  <motion.div
                    key={rowKeys[index]}
                    {...rowMotion(entranceSlot(rowKeys[index]), reduceMotion)}
                  >
                    {segment.divider ? (
                      <FeedDivider showSpeakers={showSpeakers} />
                    ) : (
                      <TranscriptRow segment={segment} showSpeakers={showSpeakers} />
                    )}
                  </motion.div>
                ))}
              </div>
              {showLivePanel && (
                <motion.div key="__live" {...LIVE_PANEL_MOTION}>
                  <TranscriptRow
                    segment={liveSegment}
                    isInterim
                    showSpeakers={showSpeakers}
                  />
                </motion.div>
              )}
              {showStatus && <FeedStatus state={state as FeedStatusState} />}
            </>
          ) : (
            <>
              <div
                role="log"
                aria-live="polite"
                aria-relevant="additions"
                aria-label="Live transcript"
                className="contents"
              >
                {segments.map((segment, index) =>
                  segment.divider ? (
                    <FeedDivider
                      key={segment.key ?? `divider-${index}`}
                      showSpeakers={showSpeakers}
                    />
                  ) : (
                    <Row
                      key={segment.key ?? `${index}-${segment.timestamp}`}
                      segment={segment}
                      showSpeakers={showSpeakers}
                    />
                  )
                )}
              </div>
              {interim && (
                <Row segment={interim} isInterim showSpeakers={showSpeakers} />
              )}
              {showStatus && <FeedStatus state={state as FeedStatusState} />}
            </>
          )}
          {!isTranscript && <div ref={bottomRef} />}
        </div>
      </ScrollArea>
    </div>
  );
};

const rowKey = (segment: LiveTranscriptionSegment, index: number) =>
  segment.key ?? `${index}-${segment.timestamp}`;

const EASE_OUT = [0.23, 1, 0.32, 1] as const;
const ROW_ENTER_S = 0.4;
const ROW_STAGGER_S = 0.05;
// More rows than this in one update is a bulk arrival (restore/replay): no cascade.
const MAX_STAGGERED_ROWS = 4;

// `animate` is set on every row: dropping it mid-entrance makes Motion revert the row.
// Motion's reducedMotion doesn't cover raw `transform` strings, so it's handled here.
function rowMotion(slot: number | null, reduceMotion: boolean) {
  const settled = reduceMotion
    ? { opacity: 1 }
    : { opacity: 1, transform: "translateY(0px)" };
  return {
    initial:
      slot === null
        ? (false as const)
        : reduceMotion
          ? { opacity: 0.5 }
          : { opacity: 0.5, transform: "translateY(4px)" },
    animate: settled,
    transition: {
      duration: ROW_ENTER_S,
      ease: EASE_OUT,
      delay: (slot ?? 0) * ROW_STAGGER_S,
    },
  };
}

// Opacity only: with the follow-scroll pin, any position animation makes the bar appear to drop in.
const LIVE_PANEL_MOTION = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: ROW_ENTER_S, ease: EASE_OUT },
} as const;

function useRowEntrances(keys: string[]): (key: string) => number | null {
  const signature = keys.join("\u0000");
  const [entrances, setEntrances] = useState(() => ({
    signature,
    seen: new Set(keys),
    slots: new Map<string, number>(),
  }));
  let current = entrances;
  if (entrances.signature !== signature) {
    const entering = keys.filter((key) => !entrances.seen.has(key));
    current = {
      signature,
      seen: new Set(keys),
      slots:
        entering.length <= MAX_STAGGERED_ROWS
          ? new Map(entering.map((key, slot) => [key, slot]))
          : new Map(),
    };
    setEntrances(current);
  }
  return (key) => current.slots.get(key) ?? null;
}

// Only an upward scroll unfollows: programmatic follow only moves down.
function useStickToBottom(
  viewportRef: React.RefObject<HTMLDivElement | null>,
  enabled: boolean
): boolean {
  const [following, setFollowing] = useState(true);
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!enabled || !viewport) return;
    let lastTop = viewport.scrollTop;
    const onScroll = () => {
      const top = viewport.scrollTop;
      const atBottom =
        viewport.scrollHeight - top - viewport.clientHeight <= STICK_THRESHOLD_PX;
      if (atBottom) setFollowing(true);
      else if (top < lastTop - 1) setFollowing(false);
      lastTop = top;
    };
    viewport.addEventListener("scroll", onScroll, { passive: true });
    return () => viewport.removeEventListener("scroll", onScroll);
  }, [viewportRef, enabled]);
  return enabled ? following : true;
}

const STICK_THRESHOLD_PX = 48;

const hasResizeObserver = () => typeof ResizeObserver !== "undefined";

// Instant, not smooth: smooth scrolls would queue up behind the next change.
function useFollowContent(
  viewportRef: React.RefObject<HTMLDivElement | null>,
  contentRef: React.RefObject<HTMLDivElement | null>,
  active: boolean
) {
  useEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!active || !viewport || !content) return;
    const pin = () => {
      viewport.scrollTop = viewport.scrollHeight;
    };
    pin();
    if (!hasResizeObserver()) return;
    const observer = new ResizeObserver(pin);
    observer.observe(content);
    return () => observer.disconnect();
  }, [viewportRef, contentRef, active]);
}

// Keyed by position so only newly inserted words play the CSS fade-in.
const LiveText = ({ text }: { text: string }) => {
  const words = text.trim().split(/\s+/).filter(Boolean);
  return (
    <>
      {words.map((word, i) => (
        <span
          key={i}
          data-word=""
          className="duration-500 ease-out animate-in fade-in-0"
        >
          {i > 0 ? " " : ""}
          {word}
        </span>
      ))}
    </>
  );
};

// Fades in only the appended tail when the same speaker's block grows.
const FinalText = ({ text }: { text: string }) => {
  const [shown, setShown] = useState({ text, stableLength: text.length });
  let current = shown;
  if (shown.text !== text) {
    current = {
      text,
      stableLength: text.startsWith(shown.text) ? shown.text.length : text.length,
    };
    setShown(current);
  }
  const { stableLength } = current;
  if (stableLength >= text.length) return <>{text}</>;
  return (
    <>
      {text.slice(0, stableLength)}
      <span
        key={stableLength}
        data-appended=""
        className="duration-500 ease-out animate-in fade-in-0"
      >
        {text.slice(stableLength)}
      </span>
    </>
  );
};

type FeedStatusState = "connecting" | "restoring" | "listening" | "paused";

const FeedRow = ({
  segment,
  isInterim = false,
  showSpeakers = true,
}: {
  segment: LiveTranscriptionSegment;
  isInterim?: boolean;
  showSpeakers?: boolean;
}) => {
  const speaker = showSpeakers ? segment.speaker : undefined;
  return (
    <div className={cn("space-y-0.5", isInterim && "text-foreground-muted")}>
      <div
        className={cn(
          "flex items-baseline gap-3",
          speaker ? "justify-between" : "justify-end"
        )}
      >
        {speaker && (
          <span
            className={cn(
              "text-xs font-semibold",
              isInterim ? "text-foreground-muted" : "text-foreground"
            )}
          >
            Speaker {speaker}:
          </span>
        )}
        <span className="shrink-0 text-[10px] text-foreground-muted">
          {segment.timestamp}
        </span>
      </div>
      <p
        className={cn(
          "text-xs",
          isInterim ? "text-foreground-muted" : "text-foreground"
        )}
      >
        {segment.text}
        {isInterim && (
          <>
            {/* Caret cursor (1.5px × 13px primary bar) + trailing "…". */}
            <span
              className="ml-0.5 inline-block h-[13px] w-[1.5px] animate-pulse bg-primary align-middle"
              aria-hidden="true"
            />
            <span className="text-foreground-muted"> …</span>
          </>
        )}
      </p>
    </div>
  );
};

/** "00:00:07" → "00:07" */
const toTranscriptTimestamp = (timestamp: string) =>
  timestamp.replace(/^00:(?=\d{2}:\d{2}$)/, "") || "0:00";

// Literal class strings so Tailwind keeps them. No red: it reads as an error.
const SPEAKER_TONES = [
  { name: "text-primary-700" },
  { name: "text-secondary-700" },
  { name: "text-success-700" },
  { name: "text-warning-700" },
] as const;

// Fixed width + tabular digits so every row's text starts at the same x.
const TIMESTAMP_BADGE = "h-6 min-w-[3.75rem] tabular-nums";

export const speakerTone = (speaker: string) => {
  const letter = speaker.trim().toUpperCase();
  const index =
    letter.length === 1 && letter >= "A" && letter <= "Z"
      ? letter.charCodeAt(0) - 65
      : [...letter].reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  return SPEAKER_TONES[index % SPEAKER_TONES.length];
};

// Plain text, never innerHTML — it's live ASR output. The interim row shows a
// wave instead of a ticking timestamp so the layout doesn't jitter.
const TranscriptRow = ({
  segment,
  isInterim = false,
  showSpeakers = true,
}: {
  segment: LiveTranscriptionSegment;
  isInterim?: boolean;
  showSpeakers?: boolean;
}) => {
  const speaker = showSpeakers ? segment.speaker : undefined;
  const tone = speaker ? speakerTone(speaker) : null;
  return (
    <div
      className={cn(
        "flex gap-4",
        // Negative margin = padding, so the panel stays aligned with rows above.
        isInterim && "-mx-3 rounded-lg bg-background-raised px-3 py-2"
      )}
      data-live={isInterim ? "" : undefined}
    >
      {/* `flex` so the wave's animating bars don't set the baseline and bob the pill. */}
      <div className="flex flex-shrink-0 items-start">
        {isInterim ? (
          <Badge variant="primary" className={TIMESTAMP_BADGE}>
            <WaveSpinner size={14} className="opacity-40" />
            <span className="sr-only">Transcribing…</span>
          </Badge>
        ) : (
          <Badge variant="primary" className={TIMESTAMP_BADGE}>
            {toTranscriptTimestamp(segment.timestamp)}
          </Badge>
        )}
      </div>
      <div className="flex-1 space-y-1">
        {speaker && tone && (
          <p className={cn("text-xs font-semibold", tone.name)}>
            Speaker {speaker}:
          </p>
        )}
        {isInterim ? (
          <p className="text-xs leading-[18px] text-foreground-muted">
            {!segment.text ? (
              <span className="italic">Listening…</span>
            ) : (
              <LiveText text={segment.text} />
            )}
          </p>
        ) : (
          <p className="text-xs leading-[18px] text-foreground">
            <FinalText text={segment.text} />
          </p>
        )}
      </div>
    </div>
  );
};

const FeedDivider = ({ showSpeakers = true }: { showSpeakers?: boolean }) => (
  <div className="flex items-center gap-2" role="separator">
    <span className="h-px flex-1 bg-border" aria-hidden="true" />
    <span className="text-[10px] text-foreground-muted">
      {showSpeakers ? "Reconnected — speaker labels may restart" : "Reconnected"}
    </span>
    <span className="h-px flex-1 bg-border" aria-hidden="true" />
  </div>
);

// Centered empty state before any turn has landed. Idle (pre-session) shows a
// static hint; an active session shows a live "Connecting…/Listening…/Paused"
// affordance so the very start of a session reads as working, not blank.
const FeedEmptyState = ({
  state,
}: {
  state: "idle" | "connecting" | "restoring" | "listening" | "paused";
}) => {
  if (state === "idle") {
    return (
      <p className="text-xs italic text-foreground-muted">
        The transcript will appear here as the assessment is recorded.
      </p>
    );
  }
  const label =
    state === "connecting"
      ? "Connecting to transcription…"
      : state === "restoring"
        ? "Restoring transcription…"
        : state === "paused"
          ? "Paused — resume to continue transcribing"
          : "Listening for speech…";
  return (
    <div
      className="flex flex-1 flex-col items-center justify-center gap-3 py-10 text-center"
      role="status"
      aria-live="polite"
    >
      {state === "paused" ? (
        <span
          className="size-2.5 rounded-full bg-foreground-muted"
          aria-hidden="true"
        />
      ) : (
        <LoadingIcon className="size-6 animate-spin-ccw" aria-hidden="true" />
      )}
      <p className="text-sm text-foreground-muted">{label}</p>
    </div>
  );
};

// Loading / status line under the feed so the gaps (connecting, or the pause
// after resume) read as "working" rather than a frozen transcript.
const FeedStatus = ({
  state,
}: {
  state: "connecting" | "restoring" | "listening" | "paused";
}) => {
  const label =
    state === "connecting"
      ? "Connecting to transcription…"
      : state === "restoring"
        ? "Restoring transcription…"
        : state === "paused"
          ? "Paused — transcription will resume when you continue"
          : "Listening…";
  return (
    <div
      className="flex items-center gap-2 text-xs text-foreground-muted"
      role="status"
      aria-live="polite"
    >
      {state === "paused" ? (
        <span
          className="size-1.5 rounded-full bg-foreground-muted"
          aria-hidden="true"
        />
      ) : (
        <LoadingIcon className="size-3.5 animate-spin-ccw" aria-hidden="true" />
      )}
      {label}
    </div>
  );
};
